import React, { useState } from "react";
import { doc, setDoc, updateDoc, arrayUnion, addDoc, collection, getDocs, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from "firebase/storage";
import { db, storage } from "../../../../backend/firebaseConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// MUI + helper components to match PatientRecord design
import { Box, Accordion, AccordionSummary, AccordionDetails, Stack, Pagination } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Text from "../../atoms/text/Text";
import Icon from "../../atoms/icon/Icon";
import Search from "../../molecules/search/Search";
import { width } from "@mui/system";
function formatDateMMDDYYYY(dateStr) {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${month}-${day}-${year}`;
}

const parseTimeToMinutes = (time) => {
  if (time === null || time === undefined) return null;
  if (typeof time === "number") return Math.floor(time);
  if (typeof time !== "string") return null;

  let str = time.trim().toLowerCase();
  if (str.length === 0) return null;

  // capture am/pm if present
  const ampmMatch = str.match(/\b(am|pm)\b/);
  let isPM = false;
  let hasAMPM = false;
  if (ampmMatch) {
    hasAMPM = true;
    isPM = ampmMatch[1] === "pm";
    str = str.replace(/\s*(am|pm)\b/, "");
  }

  // remove dots, extra whitespace
  str = str.replace(/\./g, "").trim();

  let hour = null;
  let minute = 0;

  if (str.includes(":")) {
    const [h, m] = str.split(":");
   hour = parseInt(h, 10);
    minute = parseInt(m, 10);
  } else if (/^\d{3,4}$/.test(str)) {
    // e.g. "930" or "0930" => 9:30 or 09:30
    const padded = str.padStart(4, "0");
    hour = parseInt(padded.slice(0, 2), 10);
    minute = parseInt(padded.slice(2), 10);
  } else {
    const n = parseInt(str, 10);
    if (!isNaN(n) && n >= 0 && n <= 23) {
      hour = n;
      minute = 0;
    } else {
      return null;
    }
  }

  if (isNaN(hour) || isNaN(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

  // adjust for am/pm if provided
  if (hasAMPM) {
    if (isPM && hour < 12) hour += 12;
    if (!isPM && hour === 12) hour = 0;
  }

  return hour * 60 + minute;
};


const formatTime = (minutes) => {
  if (minutes === null || minutes === undefined) return "";
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const ampm = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12;
  const formattedMinute = minute < 10 ? `0${minute}` : minute;
  return `${formattedHour}:${formattedMinute} ${ampm}`;
};

// Format number with commas
const formatNumberWithCommas = (number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const PersonnelTreatmentHistoryModal = ({ appointments, onClose, handleViewInsuranceDetails, patientName, patientUid, onRemarksSaved, userDetails }) => {
  // Function to remove parentheses from dentist names
  const formatDentistName = (dentistName) => {
    if (!dentistName) return "N/A";
    // Remove (Owner) and (Associate) from dentist names
    return dentistName.replace(/\s*\(Owner\)|\s*\(Associate\)/g, '');
  };

  const [treatmentSearchTerm, setTreatmentSearchTerm] = useState("");
  const [expandedMonths, setExpandedMonths] = useState({});

  // Group appointments by month and year
   const groupedAppointments = {};
  appointments.forEach((appt) => {
    if (!appt.date) return;
    if (appt.status !== "Completed") return;
    const dateObj = new Date(appt.date);
    const month = dateObj.toLocaleString("default", { month: "long" });
    const year = dateObj.getFullYear();
    const key = `${year}-${month}`;
    if (!groupedAppointments[key]) groupedAppointments[key] = [];
    groupedAppointments[key].push(appt);
  });
  const sortedMonthKeys = Object.keys(groupedAppointments).sort((a, b) => {
    const [yearA, monthA] = a.split("-");
    const [yearB, monthB] = b.split("-");
    const dateA = new Date(`${monthA} 1, ${yearA}`);
    const dateB = new Date(`${monthB} 1, ${yearB}`);
    return dateB - dateA;
  });

  // Year-based pagination: page 1 = latest year
  const years = Array.from(
    new Set(
      Object.keys(groupedAppointments)
        .map((k) => k.split("-")[0]) // extract year part
        .filter(Boolean)
    )
  ).sort((a, b) => Number(a) - Number(b)); // year

  const [monthsPage, setMonthsPage] = useState(1); // page indexes into `years`
  // reset to first page when search or appointments change
  React.useEffect(() => {
    setMonthsPage(1);
  }, [treatmentSearchTerm, appointments]);

  const totalMonthPages = Math.max(1, years.length || 1);
  const selectedYear = years[monthsPage - 1] || null;

  // months for the selected year, sorted newest-month-first
  const paginatedMonthKeys = selectedYear
    ? Object.keys(groupedAppointments)
        .filter((k) => k.startsWith(`${selectedYear}-`))
        .sort((a, b) => {
          const [yA, mA] = a.split("-");
          const [yB, mB] = b.split("-");
          return new Date(`${mB} 1, ${yB}`) - new Date(`${mA} 1, ${yA}`);
        })
    : [];

  const [selectedTreatment, setSelectedTreatment] = useState(
    appointments.length > 0 ? appointments[0] : null
  );
  const [dentistRemarks, setDentistRemarks] = useState(selectedTreatment?.dentistRemarks || selectedTreatment?.remarks || "");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const isSavingRef = React.useRef(false);

  // New state for image management
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [openDrawer, setOpenDrawer] = useState(false);

  React.useEffect(() => {
    setSelectedImagePaths((prev) =>
      prev.filter((path) => images.some((img) => img.path === path))
    );
  }, [images]);

  // For custom alerts
  const [snackbarConfig, setSnackbarConfig] = useState({
    isOpen: false,
    message: "",
    severity: "success",
  });

  // For delete confirmation modal
  const [deleteModalConfig, setDeleteModalConfig] = useState({
    isOpen: false,
    images: [],
  });
  const [selectedImagePaths, setSelectedImagePaths] = useState([]);

  // Add notification function
  const addNotification = async (uid, message) => {
    const notificationsRef = collection(db, "notifications", uid, "userNotifications");
    const newNotification = {
      message,
      timestamp: new Date().toISOString(),
      read: false,
    };
    try {
      await addDoc(notificationsRef, newNotification);
    } catch (error) {
      console.error("Error adding notification:", error);
    }
  };

  // Helper function to send email notification for treatment history updates
  const sendTreatmentHistoryEmailNotification = async (type, recipientEmail, recipientName, recipientRole, appointmentWindow, action, dentistRemarks = null) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const patientName = selectedTreatment?.patientDisplay || selectedTreatment?.patientName || "a patient";
      const actorName = resolveEditorLabel();
      
      const response = await fetch(`${apiUrl}/api/send-appointment-status-change`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type, // 'patient' or 'personnel'
          recipientEmail,
          recipientName,
          recipientRole,
          patientName,
          appointmentDate: selectedTreatment?.date || 'N/A',
          appointmentWindow,
          action,
          dentistRemarks,
          actorName,
          clinicName: 'VSDiamond Dental Clinic',
          isTreatmentHistoryChange: true, // Flag to indicate this is a treatment history notification
          status: 'Completed' // Required field, but not used for treatment history
        })
      });
      const result = await response.json();
      if (!result.success) {
        console.error(`Failed to send treatment history email notification:`, result.message);
      }
    } catch (error) {
      console.error(`Error sending treatment history email notification:`, error);
    }
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return "N/A";
    const [year, month, day] = dateStr.split("-");
    const dateObj = new Date(Number(year), Number(month) - 1, Number(day));
    if (Number.isNaN(dateObj.getTime())) return dateStr;
    return dateObj.toLocaleDateString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    });
  };

  const buildAppointmentWindow = (treatment) => {
    if (!treatment) return "N/A";
    const dateLabel = formatDateLong(treatment.date);
    const startMinutes = treatment.startTime
      ? parseTimeToMinutes(treatment.startTime)
      : treatment.time
      ? parseTimeToMinutes(treatment.time)
      : null;
    const endMinutes = treatment.endTime ? parseTimeToMinutes(treatment.endTime) : null;
    const startLabel =
      startMinutes !== null && startMinutes !== undefined && !Number.isNaN(startMinutes)
        ? formatTime(startMinutes)
        : "N/A";
    const endLabel =
      endMinutes !== null && endMinutes !== undefined && !Number.isNaN(endMinutes)
        ? formatTime(endMinutes)
        : null;
    const windowLabel = endLabel && endLabel !== startLabel ? `${startLabel} - ${endLabel}` : startLabel;
    return `${dateLabel} (${windowLabel})`;
  };

  const resolveEditorLabel = () => {
    const editorRole = userDetails?.role || userDetails?.userType || "";
    const editorFullName = `${userDetails?.firstName || ""} ${userDetails?.lastName || ""}`.trim();

    let editorLabel = editorFullName || "Clinic Personnel";
    if (editorRole === "DentistOwner" || editorRole === "AssociateDentist") {
      editorLabel = `Dr. ${editorLabel || "Clinic Personnel"}`.trim();
    }
    // For ClinicStaff, return name without prefix (Mr./Mrs.)
    return editorLabel;
  };

  const notifyPersonnelRemarksUpdate = async (remarksText, appointmentWindow) => {
    if (
      !userDetails ||
      !selectedTreatment ||
      (selectedTreatment.status || "").toLowerCase() !== "completed"
    )
      return;
    const usersSnapshot = await getDocs(collection(db, "users"));
    const patientNameForNotif =
      selectedTreatment.patientDisplay || selectedTreatment.patientName || "a patient";
    const editorLabel = resolveEditorLabel();

    // Get assigned dentist ID from appointment
    const assignedDentistId = selectedTreatment.dentistId || null;

    for (const docu of usersSnapshot.docs) {
      const data = docu.data();
      const userId = docu.id;
      const userRole = data.userType || data.role;

      const isClinicStaff =
        data.userType === "ClinicStaff" || data.role === "ClinicStaff";
      const isAssignedDentist =
        assignedDentistId && userId === assignedDentistId;

      if (!isClinicStaff && !isAssignedDentist) {
        continue;
      }

      const personnelMessage = `${editorLabel} has updated the dentist remarks for ${patientNameForNotif}'s completed appointment on ${appointmentWindow}.\n\nDentist Remarks: ${remarksText}`;
      await addNotification(userId, personnelMessage);

      // Send email notification if email is verified
      const emailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
      const email = data.email || data.authEmail;
      if (emailVerified && email && !email.includes("@mobileuser.dentalapp.com")) {
        const recipientName =
          data.firstName && data.lastName
            ? `${data.firstName} ${data.lastName}`
            : data.name || "User";
        await sendTreatmentHistoryEmailNotification(
          "personnel",
          email,
          recipientName,
          userRole,
          appointmentWindow,
          "remarks_updated",
          remarksText
        );
      }
    }
  };

  /** In-app + email: assigned dentist + all ClinicStaff only. Patient is notified separately. */
  const notifyPersonnelImageUpload = async (_imageCount, appointmentWindow) => {
  if (
    !userDetails ||
    !selectedTreatment ||
    (selectedTreatment.status || "").toLowerCase() !== "completed"
  )
    return;
    const usersSnapshot = await getDocs(collection(db, "users"));
    const patientNameForNotif =
      selectedTreatment.patientDisplay || selectedTreatment.patientName || "a patient";
    const editorLabel = resolveEditorLabel();

    // Get assigned dentist ID from appointment
    const assignedDentistId = selectedTreatment.dentistId || null;

    for (const docu of usersSnapshot.docs) {
      const data = docu.data();
      const userId = docu.id;
      const userRole = data.userType || data.role;

      const isClinicStaff =
        data.userType === "ClinicStaff" || data.role === "ClinicStaff";
      const isAssignedDentist =
        assignedDentistId && userId === assignedDentistId;

      if (!isClinicStaff && !isAssignedDentist) {
        continue;
      }

        const personnelMessage = `${editorLabel} has uploaded treatment image(s) for ${patientNameForNotif}'s completed appointment on ${appointmentWindow}.`;
        await addNotification(userId, personnelMessage);
        
        // Send email notification if email is verified
        const emailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
        const email = data.email || data.authEmail;
        if (emailVerified && email && !email.includes("@mobileuser.dentalapp.com")) {
          const recipientName = data.firstName && data.lastName 
            ? `${data.firstName} ${data.lastName}` 
            : (data.name || "User");
          await sendTreatmentHistoryEmailNotification('personnel', email, recipientName, userRole, appointmentWindow, 'images_uploaded');
        }
    }
  };

  /** In-app + email: assigned dentist + all ClinicStaff only. Patient is notified separately (including on removal). */
  const notifyPersonnelImageRemoval = async (_imageCount, appointmentWindow) => {
    if (
      !userDetails ||
      !selectedTreatment ||
      (selectedTreatment.status || "").toLowerCase() !== "completed"
    )
      return;
    const usersSnapshot = await getDocs(collection(db, "users"));
    const patientNameForNotif =
      selectedTreatment.patientDisplay || selectedTreatment.patientName || "a patient";
    const editorLabel = resolveEditorLabel();

    // Get assigned dentist ID from appointment
    const assignedDentistId = selectedTreatment.dentistId || null;

    for (const docu of usersSnapshot.docs) {
      const data = docu.data();
      const userId = docu.id;
      const userRole = data.userType || data.role;

      const isClinicStaff =
        data.userType === "ClinicStaff" || data.role === "ClinicStaff";
      const isAssignedDentist =
        assignedDentistId && userId === assignedDentistId;

      if (!isClinicStaff && !isAssignedDentist) {
        continue;
      }

        const personnelMessage = `${editorLabel} has removed treatment image(s) for ${patientNameForNotif}'s completed appointment on ${appointmentWindow}.`;
        await addNotification(userId, personnelMessage);
        
        // Send email notification if email is verified
        const emailVerified = data.emailVerified === "Yes" || data.emailVerified === true;
        const email = data.email || data.authEmail;
        if (emailVerified && email && !email.includes("@mobileuser.dentalapp.com")) {
          const recipientName = data.firstName && data.lastName 
            ? `${data.firstName} ${data.lastName}` 
            : (data.name || "User");
          await sendTreatmentHistoryEmailNotification('personnel', email, recipientName, userRole, appointmentWindow, 'images_removed');
        }
    }
  };

const notifyImageUpload = async (_imageCount, appointmentWindow) => {
    if (!patientUid || !selectedTreatment || (selectedTreatment.status || "").toLowerCase() !== "completed")
      return;
        const patientMessage = `Treatment image(s) for your completed appointment on ${appointmentWindow} have been uploaded.`;
    await addNotification(patientUid, patientMessage);
    
    // Send email notification to patient
    try {
      const patientDoc = await getDoc(doc(db, "users", patientUid));
      if (patientDoc.exists()) {
        const patientData = patientDoc.data();
        const emailVerified = patientData.emailVerified === "Yes" || patientData.emailVerified === true;
        const email = patientData.email || patientData.authEmail;
        if (emailVerified && email && !email.includes("@mobileuser.dentalapp.com")) {
          const patientName = patientData.firstName && patientData.lastName 
            ? `${patientData.firstName} ${patientData.lastName}` 
            : (patientData.name || "Patient");
          await sendTreatmentHistoryEmailNotification('patient', email, patientName, 'Patient', appointmentWindow, 'images_uploaded');
        }
      }
    } catch (error) {
      console.error("Error sending patient email notification for image upload:", error);
    }
  };

  const notifyImageRemoval = async (_imageCount, appointmentWindow) => {
    if (!patientUid || !selectedTreatment || (selectedTreatment.status || "").toLowerCase() !== "completed")
      return;
    const patientMessage = `Treatment image(s) for your completed appointment on ${appointmentWindow} have been removed.`;
    await addNotification(patientUid, patientMessage);
    
    // Send email notification to patient
    try {
      const patientDoc = await getDoc(doc(db, "users", patientUid));
      if (patientDoc.exists()) {
        const patientData = patientDoc.data();
        const emailVerified = patientData.emailVerified === "Yes" || patientData.emailVerified === true;
        const email = patientData.email || patientData.authEmail;
        if (emailVerified && email && !email.includes("@mobileuser.dentalapp.com")) {
          const patientName = patientData.firstName && patientData.lastName 
            ? `${patientData.firstName} ${patientData.lastName}` 
            : (patientData.name || "Patient");
          await sendTreatmentHistoryEmailNotification('patient', email, patientName, 'Patient', appointmentWindow, 'images_removed');
        }
      }
    } catch (error) {
      console.error("Error sending patient email notification for image removal:", error);
    }
  };

  // Show custom alert
  const showSnackbar = (message, severity = "success") => {
    setSnackbarConfig({
      isOpen: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbarConfig((prev) => ({ ...prev, isOpen: false }));
  };

  const toggleImageSelection = (image) => {
    setSelectedImagePaths((prev) =>
      prev.includes(image.path)
        ? prev.filter((path) => path !== image.path)
        : [...prev, image.path]
    );
  };

  const isImageSelected = (path) => selectedImagePaths.includes(path);

  const clearSelectedImages = () => setSelectedImagePaths([]);

  React.useEffect(() => {
    if (Object.keys(groupedAppointments).length > 0) {
      const allExpanded = {};
      Object.keys(groupedAppointments).forEach((monthKey) => {
        allExpanded[monthKey] = true;
      });
      setExpandedMonths(allExpanded);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments]);

  // Update dentistRemarks when selected treatment changes
  React.useEffect(() => {
    setDentistRemarks(selectedTreatment?.dentistRemarks || selectedTreatment?.remarks || "");
    // Only clear message if we're not in the middle of a save operation
    if (!isSavingRef.current) {
      setSaveMsg("");
    }
  }, [selectedTreatment]);

  // Load images when selected treatment changes
  React.useEffect(() => {
    if (selectedTreatment && patientUid) {
      loadImages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreatment, patientUid]);

  // Keyboard navigation for image modal
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showImageModal || images.length <= 1) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          const currentIndex = images.findIndex(img => img.path === selectedImage?.path);
          const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
          setSelectedImage(images[prevIndex]);
          break;
        case 'ArrowRight':
          e.preventDefault();
          const currentIdx = images.findIndex(img => img.path === selectedImage?.path);
          const nextIndex = currentIdx === images.length - 1 ? 0 : currentIdx + 1;
          setSelectedImage(images[nextIndex]);
          break;
        case 'Escape':
          setShowImageModal(false);
          break;
        default:
          break;
      }
    };

    if (showImageModal) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showImageModal, images, selectedImage]);

  // Load images from Firebase Storage
  const loadImages = async () => {
    if (!selectedTreatment || !patientUid) return;
    
    setLoadingImages(true);
    try {
      // First try to load from Firestore if images are already referenced
      if (selectedTreatment.images && Array.isArray(selectedTreatment.images)) {

        const imageUrls = await Promise.all(
          selectedTreatment.images.map(async (imgRef) => {
            if (!imgRef) return null;

            const resolvedPath = imgRef.path || imgRef.storagePath || "";
            if (imgRef.url) {
              return {
                name: imgRef.name,
                url: imgRef.url,
                path: resolvedPath,
              };
            }

            if (!resolvedPath) {
              console.error(`Image reference for ${imgRef.name} is missing a storage path.`);
              return null;
            }

            try {
              const url = await getDownloadURL(ref(storage, resolvedPath));
              return {
                name: imgRef.name,
                url,
                path: resolvedPath
              };
            } catch (error) {
              console.error(`Error loading image ${imgRef.name}:`, error);
              return null;
            }
          })
        );
        
        // Filter out any failed images
        const validImages = imageUrls.filter(img => img !== null);
        setImages(validImages);
        
        // If we have valid images, return early
        if (validImages.length > 0) return;
      }
      
      // Fallback: try to load from Storage directly
      try {
        const imagesRef = ref(storage, `treatment-images/${patientUid}/${selectedTreatment.date}/${selectedTreatment.id}`);
        const result = await listAll(imagesRef);
        
        const imageUrls = await Promise.all(
          result.items.map(async (itemRef) => {
            try {
              const url = await getDownloadURL(itemRef);
              return {
                name: itemRef.name,
                url: url,
                path: itemRef.fullPath
              };
            } catch (error) {
              console.error(`Error loading image ${itemRef.name}:`, error);
              return null;
            }
          })
        );
        
        const validImages = imageUrls.filter(img => img !== null);
        setImages(validImages);
      } catch (storageError) {
        console.error("Error accessing storage:", storageError);
        
        // Check if it's a storage configuration issue
        if (storageError.code === 'storage/unauthorized' || 
            storageError.code === 'storage/unauthenticated' ||
            storageError.message?.includes('permission') ||
            storageError.message?.includes('denied')) {
            showSnackbar("Cannot access image storage. Please check your Firebase configuration.", "error");
        } else if (storageError.code === 'storage/quota-exceeded') {
          showSnackbar("Storage quota exceeded. Cannot load images.", "error");
        } else if (storageError.message?.includes('network') || storageError.message?.includes('timeout')) {
          showSnackbar("Network error loading images. Please check your connection.", "error");
        } else {
          showSnackbar("Failed to load images from storage.", "error");
        }
        
        setImages([]);
      }
    } catch (error) {
      console.error("Error loading images:", error);
      showSnackbar("Failed to load images. Please try again.", "error");
      setImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  // Handle file upload
  const handleImageUpload = async (event) => {
    const inputElement = event.target;
    const files = Array.from(inputElement.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const appointmentWindowCached = buildAppointmentWindow(selectedTreatment);
      const uploadedImages = [];
      const errors = [];
      let totalProgress = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          // Validate file type - only allow JPG, PNG, or SVG
          const allowedMimeTypes = ["image/jpeg", "image/jpg", "image/png", "image/svg+xml"];
          const allowedExtensions = [".jpg", ".jpeg", ".png", ".svg"];

          const fileType = (file.type || "").toLowerCase();
          const fileNameLower = (file.name || "").toLowerCase();
          const hasAllowedExtension = allowedExtensions.some((ext) =>
            fileNameLower.endsWith(ext)
          );

          if (!allowedMimeTypes.includes(fileType) || !hasAllowedExtension) {
            errors.push(`${file.name} is not a supported image type. Only JPG, PNG, or SVG are allowed.`);
            continue;
          }

          // Validate file size (5MB limit)
          if (file.size > 5 * 1024 * 1024) {
            errors.push(`${file.name} is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Max size: 5MB.`);
            continue;
          }

          const timestamp = Date.now();
          const fileName = `${timestamp}_${file.name}`;
          const imageRef = ref(
            storage,
            `treatment-images/${patientUid}/${selectedTreatment.date}/${selectedTreatment.id}/${fileName}`
          );
          
          // Upload file with timeout
          const uploadPromise = uploadBytes(imageRef, file);
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Upload timeout - storage may not be responding')), 10000)
          );
          
          const snapshot = await Promise.race([uploadPromise, timeoutPromise]);
          const downloadURL = await getDownloadURL(snapshot.ref);
          
          uploadedImages.push({
            name: fileName,
            url: downloadURL,
            path: snapshot.ref.fullPath
          });

          // Update progress
          totalProgress = ((i + 1) / files.length) * 100;
          setUploadProgress(totalProgress);
        } catch (fileError) {
          console.error(`Error uploading ${file.name}:`, fileError);
          
          let specificError = `Failed to upload ${file.name}.`;
          
          if (fileError.code === 'storage/unauthorized') {
            specificError = `${file.name}: Storage access denied`;
          } else if (fileError.code === 'storage/quota-exceeded') {
            specificError = `${file.name}: Storage quota exceeded`;
          } else if (fileError.code === 'storage/unauthenticated') {
            specificError = `${file.name}: Storage authentication failed`;
          } else if (fileError.message?.includes('timeout')) {
            specificError = `${file.name}: Upload timeout - storage not responding`;
          } else if (fileError.message?.includes('network')) {
            specificError = `${file.name}: Network error during upload`;
          } else if (fileError.message?.includes('permission')) {
            specificError = `${file.name}: Permission denied`;
          }
          
          errors.push(specificError);
          
          // Still update progress even for failed uploads
          totalProgress = ((i + 1) / files.length) * 100;
          setUploadProgress(totalProgress);
        }
      }

      // Show results
      if (uploadedImages.length > 0) {
        try {
          // Update Firestore to track image references
          // Use arrayUnion to append new images to existing array
          const imageReferences = uploadedImages.map(img => ({
            name: img.name,
            path: img.path
          }));
          
          await updateDoc(doc(db, "appointments", selectedTreatment.date), {
            [`${selectedTreatment.id}.images`]: arrayUnion(...imageReferences)
          });

          // Update local state
          setImages(prev => [...prev, ...uploadedImages]);
          clearSelectedImages();

          // Notify parent to update local state
          if (onRemarksSaved) {
            const updatedImagesArray = [...images, ...uploadedImages];
            onRemarksSaved({ 
              id: selectedTreatment.id, 
              date: selectedTreatment.date, 
              dentistRemarks: selectedTreatment.dentistRemarks || selectedTreatment.remarks || "",
              images: updatedImagesArray
            });
          }

          // Show success message and notify patient
          if (errors.length === 0) {
            showSnackbar(`Successfully uploaded ${uploadedImages.length} image(s)`, "success");
          } else {
            // Count invalid file format errors
            const invalidFileErrors = errors.filter(err => err.includes('is not an image file'));
            if (invalidFileErrors.length > 0) {
              // Partial success with invalid files
              showSnackbar(
                `Uploaded ${uploadedImages.length} image(s) successfully. The other images (${invalidFileErrors.length}) failed due to invalid file format.`,
                "success"
              );
            } else {
              // Other errors (size, network, etc.)
              showSnackbar(
                `Uploaded ${uploadedImages.length} image(s) successfully. Some images failed to upload.`,
                "success"
              );
            }
          }
          await notifyImageUpload(uploadedImages.length, appointmentWindowCached);
          if (userDetails) {
            await notifyPersonnelImageUpload(uploadedImages.length, appointmentWindowCached);
          }
        } catch (firestoreError) {
          console.error("Error updating Firestore:", firestoreError);
          showSnackbar(
            `Images uploaded but failed to save references to database.\n\nError: ${firestoreError.message}`,
            "warning"
          );
        }
      } else {
        // No images were uploaded successfully
        // Check if all errors are invalid file format errors
        const allInvalidFiles = errors.every(err => err.includes('is not an image file'));
        
        if (allInvalidFiles) {
          showSnackbar("Failed to upload image(s), invalid image file.", "error");
        } else {
          let errorMessage = "Failed to upload image(s). Please try again.";
          
          if (errors.some(err => err.includes('timeout'))) {
            errorMessage = "Firebase Storage is not responding. Please check your configuration or try again later.";
          } else if (errors.some(err => err.includes('permission'))) {
            errorMessage = "You don't have permission to upload to Firebase Storage. Please check your security rules.";
          } else if (errors.some(err => err.includes('quota'))) {
            errorMessage = "Your Firebase Storage quota has been exceeded. Please upgrade your plan.";
          }
          
          showSnackbar(errorMessage, "error");
        }
      }

      setUploadProgress(0);
    } catch (error) {
      console.error("Error uploading images:", error);
      
      let errorMessage = "Failed to upload images. Please try again.";
      let errorTitle = "Upload Error";
      
      if (error.code === 'storage/unauthorized') {
        errorMessage = "Firebase Storage access denied. Please check your configuration and security rules.";
        errorTitle = "Storage Access Denied";
      } else if (error.code === 'storage/quota-exceeded') {
        errorMessage = "Firebase Storage quota exceeded. Please upgrade your plan or contact support.";
        errorTitle = "Storage Quota Exceeded";
      } else if (error.code === 'storage/unauthenticated') {
        errorMessage = "Firebase Storage authentication failed. Please check your credentials.";
        errorTitle = "Authentication Failed";
      } else if (error.message?.includes('timeout')) {
        errorMessage = "Firebase Storage is not responding. Please check your configuration or try again later.";
        errorTitle = "Storage Timeout";
      } else if (error.message?.includes('network')) {
        errorMessage = "Network error connecting to Firebase Storage. Please check your internet connection.";
        errorTitle = "Network Error";
      }
      
      showSnackbar(errorMessage, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (inputElement) {
        inputElement.value = "";
      }
    }
  };

  // Delete image

  // View image in modal
  const handleViewImage = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      handleFiles(files);
    }
  };

  const handleFiles = (files) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      showSnackbar("Please select only image files.", "warning");
      return;
    }
    
    // Create a fake event object for the existing upload handler
    const fakeEvent = { target: { files: imageFiles } };
    handleImageUpload(fakeEvent);
  };

  const handleDeleteSelectedImages = () => {
    if (selectedImagePaths.length === 0) return;
    const imagesToDelete = images.filter((img) => selectedImagePaths.includes(img.path));
    if (imagesToDelete.length === 0) return;
    setDeleteModalConfig({
      isOpen: true,
      images: imagesToDelete,
    });
  };

  // Save dentistRemarks to Firebase
  const handleSaveRemarks = async () => {
    if (!selectedTreatment || !patientUid) return;
    setSaving(true);
    isSavingRef.current = true;
    const appointmentWindow = buildAppointmentWindow(selectedTreatment);
    const trimmedRemarks =
      typeof dentistRemarks === "string" && dentistRemarks.trim()
        ? dentistRemarks.trim()
        : "";
    const resolvedDentistRemarks = trimmedRemarks || "None";
    try {
      // Find the appointment in Firebase by date and id (nested structure)
      const apptDate = selectedTreatment.date;
      const apptId = selectedTreatment.id;
      const apptRef = doc(db, "appointments", apptDate);
      
      // Check if dentistRemarks actually changed
      const originalDentistRemarks = selectedTreatment.dentistRemarks || selectedTreatment.remarks || "";
      const remarksChanged = originalDentistRemarks !== dentistRemarks;
      
      await setDoc(apptRef, { [apptId]: { ...selectedTreatment, dentistRemarks } }, { merge: true });
      setSaveMsg("Remarks saved!");
      
      // Notify patient about remarks update if remarks changed
      if (remarksChanged && (selectedTreatment.status || "").toLowerCase() === "completed") {
        const patientMessage = `Your dentist remarks for your completed appointment on ${appointmentWindow} have been updated.\n\nDentist Remarks: ${resolvedDentistRemarks}`;
        await addNotification(patientUid, patientMessage);
        
        // Send email notification to patient
        try {
          const patientDoc = await getDoc(doc(db, "users", patientUid));
          if (patientDoc.exists()) {
            const patientData = patientDoc.data();
            const emailVerified = patientData.emailVerified === "Yes" || patientData.emailVerified === true;
            const email = patientData.email || patientData.authEmail;
            if (emailVerified && email && !email.includes("@mobileuser.dentalapp.com")) {
              const patientName = patientData.firstName && patientData.lastName 
                ? `${patientData.firstName} ${patientData.lastName}` 
                : (patientData.name || "Patient");
              await sendTreatmentHistoryEmailNotification('patient', email, patientName, 'Patient', appointmentWindow, 'remarks_updated', resolvedDentistRemarks);
            }
          }
        } catch (error) {
          console.error("Error sending patient email notification for remarks update:", error);
        }
        
        if (userDetails) {
          await notifyPersonnelRemarksUpdate(resolvedDentistRemarks, appointmentWindow);
        }
      }
      
      setTimeout(() => {
        setSaveMsg("");
        isSavingRef.current = false;
      }, 2500);
      // Notify parent to update local state
      if (onRemarksSaved) {
        onRemarksSaved({ id: apptId, date: apptDate, dentistRemarks, images });
      }
      setSelectedTreatment({ ...selectedTreatment, dentistRemarks, images });
    } catch (error) {
      setSaveMsg("Failed to save remarks.");
      isSavingRef.current = false;
    }
    setSaving(false);
  };

  // Confirm delete image
  const handleConfirmDelete = async () => {
    const imagesToDelete = deleteModalConfig.images || [];
    if (!imagesToDelete.length || !selectedTreatment || !patientUid) return;

    const pathsToDelete = new Set(imagesToDelete.map((img) => img.path));
    let deletionSucceeded = false;

    try {
      for (const imageToDelete of imagesToDelete) {
        const imageRef = ref(storage, imageToDelete.path);
        await deleteObject(imageRef);
      }

      const updatedImages = images.filter((img) => !pathsToDelete.has(img.path));
      setImages(updatedImages);

      try {
        await updateDoc(doc(db, "appointments", selectedTreatment.date), {
          [`${selectedTreatment.id}.images`]: updatedImages.map((img) => ({
            name: img.name,
            path: img.path
          }))
        });

        if (onRemarksSaved) {
          onRemarksSaved({
            id: selectedTreatment.id,
            date: selectedTreatment.date,
            dentistRemarks: selectedTreatment.dentistRemarks || selectedTreatment.remarks || "",
            images: updatedImages.map((img) => ({
              name: img.name,
              path: img.path
            }))
          });
        }

        setSelectedTreatment((prev) =>
          prev
            ? {
                ...prev,
                images: updatedImages.map((img) => ({
                  name: img.name,
                  path: img.path
                }))
              }
            : prev
        );

        showSnackbar(`Successfully removed ${imagesToDelete.length} image(s)`, "success");
        const appointmentWindow = buildAppointmentWindow(selectedTreatment);
        await notifyImageRemoval(imagesToDelete.length, appointmentWindow);
        await notifyPersonnelImageRemoval(imagesToDelete.length, appointmentWindow);
        deletionSucceeded = true;
      } catch (firestoreError) {
        console.error("Error updating Firestore after delete:", firestoreError);
        showSnackbar("Image(s) deleted from storage but failed to update database reference.", "warning");
      }
    } catch (error) {
      console.error("Error deleting image:", error);

      let errorMessage = "Failed to delete image(s). Please try again.";
      if (error.code === 'storage/object-not-found') {
        errorMessage = "Image not found in storage. It may have been already deleted.";
      } else if (error.code === 'storage/unauthorized') {
        errorMessage = "Permission denied to delete image. Please check your storage rules.";
      } else if (error.code === 'storage/unauthenticated') {
        errorMessage = "Authentication failed. Please check your credentials.";
      } else if (error.message?.includes('network') || error.message?.includes('timeout')) {
        errorMessage = "Network error during delete. Please check your connection.";
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setDeleteModalConfig({ isOpen: false, images: [] });
      if (deletionSucceeded) {
        setSelectedImagePaths((prev) => prev.filter((path) => !pathsToDelete.has(path)));
      }
    }
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setDeleteModalConfig({ isOpen: false, images: [] });
  };

 return (
    <Box sx={{
      maxHeight: '80vh',
      width: "100%",
      display: 'flex',
      flexDirection: 'column',
      // background: '#e8b971ff',
    }}>

      {/* Sticky Header Row with Search */}
      <Box sx={{
        top: 0,
        zIndex: 20,
        borderRadius: "10px",
        mt: "10px",
        display: 'flex',
        alignItems: 'center',
        justifyContent: {
          xs: "right",
          sm: "right",
          md: "right",
          lg: "right",
          xl: "right",
        },
        textAlign: "center",
        padding: {
              xs: "15px",
              sm: "15px",
              md: "15px 30px",
              lg: "15px 30px",
              xl: "15px 30px",
            },
          mb: "10px"
      }}>
        

        <Box 
          sx={{ 
            display: {
              xs: "flex",
              sm: "flex",
              md: "flex",
              lg: "flex",
              xl: "flex",
            }, 
            alignItems: "center", 
            gap: "12px"
          }}>
            
                <Box 
                sx={{
          display: {
            xs: !selectedTreatment ? "none" : "flex",
            sm: !selectedTreatment ? "none" : "flex",
            md: !selectedTreatment ? "none" : "flex",
            lg: "none",
            xl: "none",
          },
                cursor: "pointer"
                }}

                onClick={e => setSelectedTreatment(false)} position="absolute" left="40px">{"Back"}</Box>
                <Box
                  sx={{
          display: {
            xs: selectedTreatment ? "none" : "flex",
            sm: selectedTreatment ? "none" : "flex",
            md: selectedTreatment ? "none" : "flex",
            lg: "flex",
            xl: "flex",
          },
                  }}  
                >
                  <Search
                    handleSearch={e => setTreatmentSearchTerm(e.target.value)}
                    value={treatmentSearchTerm}/>
                </Box>
          {/* Close button - top right, circular */}
          <button
            onClick={onClose}
            style={{
              marginLeft: 16,
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: '1px solid rgba(44,37,30,0.06)',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#2E2D2D',
              zIndex: 10,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#C5A572';
              e.currentTarget.style.color = '#FEFEFE';
              e.currentTarget.style.borderColor = '#C5A572';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fff';
              e.currentTarget.style.color = '#2E2D2D';
              e.currentTarget.style.borderColor = 'rgba(44,37,30,0.06)';
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </Box>
      </Box>

      <Box 
        sx={{ 
          display: "flex", 
          gap: "16px", 
          justifyContent: "center", 
          alignItems: "flex-start", 
          padding: "0", 
          minHeight: 500,
          overflowY: "auto"
        }}
      >
        
        {/* Left: List of Treatments with MUI Accordions (PatientRecord style) */}
       <Box sx={{
          display: {
            xs: selectedTreatment ? "none" : "flex",
            sm: selectedTreatment ? "none" : "flex",
            md: selectedTreatment ? "none" : "flex",
            lg: "flex",
            xl: "flex",
          },
          flex: 2,
          width: "100%",
          minWidth: 0,
          // background: "#d6a85eff",
          // borderRadius: 3,
          // boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
          // p: 1,                                 // slightly less padding
          // ml: 2,
          mr: 0,
          overflowY: "auto",
          height: "calc(80vh - 180px)",
          maxHeight: "calc(80vh - 180px)",
          // border: "1px solid #ede7df"
        }}>

          {sortedMonthKeys.length === 0 ? (
            <p style={{ textAlign: "center" }}>No treatment history found.</p>
          ) : (
            <Box
              sx={{
                width: "100%"
              }}>

                <Box
                  sx={{
                    height: "90%",
                    overflow: "auto"
                  }}>
              {paginatedMonthKeys
                .filter(monthKey => {
                  const dateMatches = groupedAppointments[monthKey].some(appt => {
                    const dateStr = appt.date ? formatDateMMDDYYYY(appt.date) : "";
                    const serviceStr = Array.isArray(appt.services)
                      ? appt.services.map(s => (typeof s === 'object' && s.name ? s.name : s)).join(", ")
                      : (appt.services || "");
                    const monthStr = monthKey.toLowerCase();
                    return (
                      dateStr.includes(treatmentSearchTerm) ||
                      serviceStr.toLowerCase().includes(treatmentSearchTerm.toLowerCase()) ||
                      monthStr.includes(treatmentSearchTerm.toLowerCase())
                    );
                  });
                  return dateMatches || monthKey.toLowerCase().includes(treatmentSearchTerm.toLowerCase());
                })
                .map((monthKey) => {
                  const filteredAppointments = groupedAppointments[monthKey]
                    .filter(appt => {
                      const dateStr = appt.date ? formatDateMMDDYYYY(appt.date) : "";
                      const serviceStr = Array.isArray(appt.services)
                        ? appt.services.map(s => (typeof s === 'object' && s.name ? s.name : s)).join(", ")
                        : (appt.services || "");
                      const monthStr = monthKey.toLowerCase();
                      return (
                        dateStr.includes(treatmentSearchTerm) ||
                        serviceStr.toLowerCase().includes(treatmentSearchTerm.toLowerCase()) ||
                        monthStr.includes(treatmentSearchTerm.toLowerCase())
                      );
                    });

                  return (
                    <Box key={monthKey} sx={{ mb: 1,
          width: "100%"}} >
                      <Accordion
                        elevation={0}
                        expanded={expandedMonths[monthKey] === true}
                        onChange={() => setExpandedMonths(prev => ({ ...prev, [monthKey]: !prev[monthKey] }))}
                        sx={{
                          width: "100%",
                          borderRadius: 1,
                          backgroundColor: "transparent",
                          "&.MuiPaper-root": { boxShadow: "none" },
                          "& .MuiAccordionSummary-root": {
                            px: 2,
                          },
                          // reduce top spacing so first item sits closer to the header
                          "& .MuiAccordionDetails-root": {
                            backgroundColor: "transparent",
                            pt: 0,
                            pb: 2,
                          },
                        }}
                      >
                        <AccordionSummary
                          id={`month-${monthKey}-header`}
                          aria-controls={`month-${monthKey}-content`}
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            borderBottom: "1px solid rgba(88, 85, 78, 0.08)",
                            "&:hover": { backgroundColor: "rgba(46, 45, 45, 0.4)" },
                            "& .MuiTypography-root": { color: "#3d342b" },
                          }}
                        >
                          {(() => {
                          const [year, month] = monthKey.split("-");
                          return (
                            <Text
                              text={`${month} - ${year}`}
                              variant="headerh2"
                            />
                          );
                        })()}
                        </AccordionSummary>
                        <AccordionDetails>
                          {expandedMonths[monthKey] && (
                            <div>
                              {filteredAppointments.length > 0 ? (
                            filteredAppointments
                                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                                  .map((appt) => {
                                    const isSelected = selectedTreatment && selectedTreatment.id === appt.id && selectedTreatment.date === appt.date;
                                   return (
                                     <Stack
                                        direction="row"
                                        spacing={2}
                                        key={appt.id || `${appt.date}-${appt.startTime || appt.time || ""}`}
                                        alignItems="center" // ensure icon + text are vertically centered
                                        sx={{ mb: 1 }}
                                      >
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: 48 }}>
                                          <Icon src={require("../../../../assets/tooth.png")} size="history" />
                                        </Box>

                                       <Box
                                         onClick={() => setSelectedTreatment(appt)}
                                         sx={{
                                           borderRadius: "10px",
                                           padding: "10px",
                                           background: isSelected ? "rgba(46, 45, 45, 0.3)" : "transparent",
                                           width: "100%",
                                           cursor: "pointer",
                                           transition: "all 0.3s",
                                           display: "flex",
                                           flexDirection: "column",
                                           justifyContent: "center",
                                           alignItems: "flex-start",       // ensure left alignment
                                           gap: "4px",                      // consistent spacing between lines
                                           "&:hover": {
                                             backgroundColor: !isSelected ? "rgba(46, 45, 45, 0.2)" : undefined,
                                           },
                                         }}
                                       >
                                          <Text
                                            variant="historyService"
                                            text={
                                              Array.isArray(appt.services)
                                                ? appt.services
                                                    .map((s) => (typeof s === "object" && s.name ? s.name : s))
                                                    .join(", ")
                                                : appt.services || "Service"
                                            }
                                            style={{ margin: 0, lineHeight: "1.2" }}
                                          />

                                          <Text
                                            variant="historyDateTime"
                                            text={appt.date ? formatDateMMDDYYYY(appt.date) : "N/A"}
                                            style={{ margin: 0, lineHeight: "1.2", opacity: 0.9 }}
                                          />

                                          <Text
                                            variant="historyDateTime"
                                            text={
                                              (() => {
                                                const startRaw = appt.startTime || appt.time;
                                                const start = parseTimeToMinutes(startRaw);
                                                const endRaw = appt.endTime;
                                                const end = endRaw ? parseTimeToMinutes(endRaw) : (appt.duration && start !== null ? start + Number(appt.duration) : null);
                                                if (start === null) return "N/A";
                                                return end !== null ? `${formatTime(start)} - ${formatTime(end)}` : formatTime(start);
                                              })()
                                            }
                                            style={{ margin: 0, lineHeight: "1.2", fontWeight: 500 }}
                                          />
                                        </Box>
                                      </Stack>
                                    );
                                  })
                                ) : (
                                <div style={{ color: "#888", textAlign: "center", marginTop: "8px" }}>
                                    No appointments found for this month.
                                  </div>
                              )}
                            </div>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  );
                })}
                </Box>

              {/* MUI Pagination for month groups (left column) */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <Pagination
                  count={totalMonthPages}
                  page={monthsPage}
                  onChange={(e, p) => setMonthsPage(p)}
                  shape="rounded"
                  variant="outlined"
                  color="primary"
                  siblingCount={0}
                  boundaryCount={1}
                />
              </Box>
            </Box>
          )}
        </Box>

        
        {/* Right: Treatment Details */}
        <Box sx={{
          flex: 1,
          // minWidth: 380,
          width: {
            xs: "100%",
            sm: "100%",
            md: "100%",
            lg: "100%",
            xl: "100%",
          },
          background: "transparent",
          display: {
            xs: selectedTreatment ? "flex" : "none",
            sm: selectedTreatment ? "flex" : "none",
            md: selectedTreatment ? "flex" : "none",
            lg: "flex",
            xl: "flex",
          },
          justifyContent: "flex-start",
          alignItems: "flex-start",
        }}>
            {selectedTreatment ? (
              <Box sx={{
                width: "100%",
                background: "#f9f6f2",
                boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                marginTop: 0,
                border: '2px solid #ede7df',
                padding: "18px",
                borderRadius: 3
              }}>
                <span 
                  style={{ 
                    fontWeight: "normal", 
                    fontSize: "19px", 
                    color: "#333", 
                    }}>
                      Patient:
                </span>
                <Text
                  text={patientName}
                  variant="patientRecordName"
                />
                {/* Details */}
                <div 
                  style={{ 
                    background: "#FEFEFE",
                    padding: "10px",
                    borderRadius: "8px",
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: "#2c251e",
                    boxShadow: "0 1px 6px rgba(0,0,0,0.1)",
                    marginTop: "18px"
                  }}>
                  <span style={{ fontWeight: "bold", fontSize: "19px", color: "#333", display: "block", marginBottom: "8px" }}>Details</span>
                  <div style={{ display: "table", width: "100%", borderSpacing: "0 8px" }}>
                    <div style={{ display: "table-row", marginBottom: "10px" }}>
                      <span style={{ display: "table-cell", fontWeight: "normal", paddingRight: "10px", verticalAlign: "top" }}>Service:</span>
                      <span style={{ display: "table-cell" }}>
                        {Array.isArray(selectedTreatment.services) ? selectedTreatment.services.map(service => 
                          typeof service === 'object' && service.name ? service.name : service
                        ).join(", ") : (selectedTreatment.services || "Service")}
                      </span>
                    </div>
                    <div style={{ display: "table-row", marginBottom: "10px" }}>
                      <span style={{ display: "table-cell", fontWeight: "normal", paddingRight: "10px", verticalAlign: "top" }}>Date:</span>
                      <span style={{ display: "table-cell" }}>
                        {selectedTreatment.date ? formatDateMMDDYYYY(selectedTreatment.date) : "N/A"}
                      </span>
                    </div>
                    <div style={{ display: "table-row", marginBottom: "10px" }}>
                      <span style={{ display: "table-cell", fontWeight: "normal", paddingRight: "10px", verticalAlign: "top" }}>Time:</span>
                      <span style={{ display: "table-cell" }}>
                        {(() => {
                          if (selectedTreatment.startTime) {
                            const start = parseTimeToMinutes(selectedTreatment.startTime);
                            const end = selectedTreatment.endTime
                              ? parseTimeToMinutes(selectedTreatment.endTime)
                              : selectedTreatment.duration
                                ? start + Number(selectedTreatment.duration)
                                : null;
                            return end !== null
                              ? `${formatTime(start)} - ${formatTime(end)}`
                              : formatTime(start);
                          } else if (selectedTreatment.time) {
                            const start = parseTimeToMinutes(selectedTreatment.time);
                            const end = selectedTreatment.duration
                              ? start + Number(selectedTreatment.duration)
                              : null;
                            return end !== null
                              ? `${formatTime(start)} - ${formatTime(end)}`
                              : start !== null
                                ? formatTime(start)
                                : "N/A";
                          } else {
                            return "N/A";
                          }
                        })()}
                      </span>
                    </div>
                    <div style={{ display: "table-row", marginBottom: "10px" }}>
                      <span style={{ display: "table-cell", fontWeight: "normal", paddingRight: "10px", verticalAlign: "top" }}>Dentist:</span>
                      <span style={{ display: "table-cell" }}>{formatDentistName(selectedTreatment.dentist)}</span>
                    </div>
                  </div>
                </div>

                {selectedTreatment.insuranceDetails && (
                  <>
                    {/* View Insurance */}
                    <div 
                      style={{ 
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        padding: "8px",
                        alignItems: "center",
                        marginBottom: "10px", 
                        marginTop: "10px",

                      }}>
                      <span 
                        style={{ 
                          fontWeight: "bold", 
                          fontSize: "19px", 
                          color: "#333", 
                          }}>
                            Insurance
                      </span>
                      <div>
                        <button
                          onClick={() => handleViewInsuranceDetails(selectedTreatment)}
                          style={{
                            background: "#007BFF",
                            color: "white",
                            border: "none",
                            padding: "8px 18px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "15px",
                            fontWeight: "bold"
                          }}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* Editable Remarks */}
                <Accordion
                  elevation={0}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: "transparent",
                    "&.MuiPaper-root": { boxShadow: "none" },
                    "& .MuiAccordionSummary-root": {
                      px: 1
                    },
                    "& .MuiAccordionDetails-root": {
                      backgroundColor: "transparent",
                      pt: 0,
                      pb: 2
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      "& .MuiTypography-root": { color: "#3d342b" },
                    }}
                  >
                    <span style={{ fontWeight: "bold", fontSize: "19px", color: "#333" }}>
                      Remarks
                    </span>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div
                      style={{
                        height: "80%",
                        width: "100%",
                      }}
                    >
                      <textarea
                        value={dentistRemarks}
                        onChange={(e) => setDentistRemarks(e.target.value)}
                        rows={3}
                        style={{
                          width: "-webkit-fill-available",
                          height: "100px",
                          fontSize: 15,
                          padding: 8,
                          borderRadius: 6,
                          border: "1px solid #ccc",
                          resize: "vertical",
                          minHeight: 100,
                        }}
                        placeholder="Enter dentist remarks..."
                      />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          marginTop: 8,
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          onClick={handleSaveRemarks}
                          disabled={saving}
                          style={{
                            background: "#1976d2",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            padding: "6px 18px",
                            fontWeight: 600,
                            fontSize: 15,
                            cursor: "pointer",
                          }}
                        >
                          {saving ? "Saving..." : "Save Remarks"}
                        </button>
                        {saveMsg && (
                          <div
                            style={{
                              background: saveMsg === "Remarks saved!" ? "#4caf50" : "#f44336",
                              color: "#fff",
                              padding: "6px 12px",
                              borderRadius: 6,
                              fontWeight: 600,
                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                              minWidth: 120,
                              maxWidth: 200,
                            }}
                          >
                            {saveMsg}
                            <button
                              onClick={() => {
                                setSaveMsg("");
                                isSavingRef.current = false;
                              }}
                              style={{
                                background: "transparent",
                                border: "none",
                                color: "#fff",
                                fontSize: 14,
                                cursor: "pointer",
                                padding: "0 4px",
                                borderRadius: "50%",
                                width: 16,
                                height: 16,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                marginLeft: "auto",
                              }}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
                        
                {/* Invoice */}
                <Accordion
                  elevation={0}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: "transparent",
                    "&.MuiPaper-root": { boxShadow: "none" },
                    "& .MuiAccordionSummary-root": {
                      px: 1,
                    },
                    "& .MuiAccordionDetails-root": {
                      backgroundColor: "transparent",
                      pt: 0,
                      pb: 2,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      "& .MuiTypography-root": { color: "#3d342b" },
                    }}
                  >
                    <span style={{ fontWeight: "bold", fontSize: "19px", color: "#333" }}>
                      Invoice
                    </span>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div>
                      {/* Individual Services with Prices */}
                      {Array.isArray(selectedTreatment.services) && selectedTreatment.services.length > 0 ? (
                        selectedTreatment.services.map((service, index) => {
                          const serviceName = typeof service === 'object' && service.name ? service.name : service;
                          const servicePrice = typeof service === 'object' && service.price ? Number(service.price) : 0;

                          return (
                            <div key={index} style={{ marginBottom: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span style={{ fontWeight: "500" }}>{serviceName}:</span>
                              <span style={{ fontWeight: "bold" }}>₱{formatNumberWithCommas(servicePrice)}</span>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ marginBottom: "8px", color: "#666", fontStyle: "italic" }}>
                          No services listed
                        </div>
                      )}

                      {/* Total Bill */}
                      <hr style={{ border: 'none', borderBottom: '1px solid #ddd', margin: '12px 0' }} />
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
                        <span style={{ fontWeight: "bold", fontSize: "16px" }}>Total Bill:</span>
                        <span style={{ fontWeight: "bold", fontSize: "16px", color: "#1976d2" }}>
                          {selectedTreatment.totalBill ? `₱${formatNumberWithCommas(selectedTreatment.totalBill)}` : "N/A"}
                        </span>
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
                
                {/* Images Section */}
                <Accordion
                  elevation={0}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: "transparent",
                    "&.MuiPaper-root": { boxShadow: "none" },
                    "& .MuiAccordionSummary-root": {
                      px: 1,
                    },
                    "& .MuiAccordionDetails-root": {
                      backgroundColor: "transparent",
                      pt: 0,
                      pb: 2,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      "& .MuiTypography-root": { color: "#3d342b" },
                    }}
                  >
                    <span style={{ fontWeight: "bold", fontSize: "19px", color: "#333" }}>
                      Images
                    </span>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div style={{ marginBottom: "32px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                        {images.length > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ 
                              fontSize: "14px", 
                              color: "#666", 
                              background: "#e9ecef", 
                              padding: "4px 8px", 
                              borderRadius: "12px" 
                            }}>
                              {images.length} image{images.length !== 1 ? 's' : ''}
                            </span>
                            {selectedImagePaths.length > 0 && (
                              <button
                                onClick={handleDeleteSelectedImages}
                                style={{
                                  background: "#dc3545",
                                  color: "white",
                                  border: "none",
                                  padding: "6px 12px",
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease"
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                              >
                                Delete Selected ({selectedImagePaths.length})
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Upload Section */}
                      <div style={{ marginBottom: "20px" }}>
                        
                      <input
                        type="file"
                        multiple
                        accept=".jpg,.jpeg,.png,.svg"
                        onChange={handleImageUpload}
                        disabled={uploading}
                        style={{ display: "none" }}
                        id="image-upload"
                      />
                        
                        {/* Drag and Drop Zone */}
                        <div
                          onDragEnter={handleDragIn}
                          onDragLeave={handleDragOut}
                          onDragOver={handleDrag}
                          onDrop={handleDrop}
                          style={{
                            border: `2px dashed ${dragActive ? "#28a745" : "#dee2e6"}`,
                            borderRadius: "8px",
                            padding: "20px",
                            textAlign: "center",
                            background: dragActive ? "#f8fff8" : "#f8f9fa",
                            transition: "all 0.3s ease",
                            marginBottom: "15px",
                            opacity: 1,
                            cursor: "pointer"
                          }}
                        >
                          <div style={{ fontSize: "16px", color: "#666", marginBottom: "10px" }}>
                            {dragActive ? "Drop images here" : "Drag and drop images here"}
                          </div>
                          <div style={{ fontSize: "14px", color: "#999", marginBottom: "15px" }}>
                            or
                          </div>
                          <label
                            htmlFor="image-upload"
                            style={{
                              background: "#28a745",
                              color: "white",
                              border: "none",
                              padding: "10px 20px",
                              borderRadius: "6px",
                              cursor: (uploading) ? "not-allowed" : "pointer",
                              fontSize: "15px",
                              fontWeight: "bold",
                              display: "inline-block",
                              opacity: (uploading) ? 0.6 : 1,
                              transition: "all 0.3s ease"
                            }}
                          >
                            {uploading ? "Uploading..." : "Choose Files"}
                          </label>
                        </div>
                        
                        {uploading && (
                          <div style={{ marginTop: "10px" }}>
                            <div style={{ 
                              width: "100%", 
                              height: "6px", 
                              background: "#e0e0e0", 
                              borderRadius: "3px",
                              overflow: "hidden"
                            }}>
                              <div style={{
                                width: `${uploadProgress}%`,
                                height: "100%",
                                background: uploadProgress > 0 ? "#28a745" : "#ffc107",
                                transition: "width 0.3s ease"
                              }} />
                            </div>
                            <div style={{ 
                              display: "flex", 
                              justifyContent: "space-between", 
                              alignItems: "center", 
                              marginTop: "5px" 
                            }}>
                              <span style={{ fontSize: "12px", color: "#666" }}>
                                {Math.round(uploadProgress)}% Complete
                              </span>
                              <span style={{ 
                                fontSize: "12px", 
                                color: uploadProgress > 0 ? "#28a745" : "#ffc107", 
                                fontWeight: "500" 
                              }}>
                                {uploadProgress === 0 ? "Initializing upload..." : "Uploading images..."}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        <div style={{ fontSize: "12px", color: "#666", marginTop: "8px", textAlign: "center" }}>
                          <div>Supported formats: JPG, PNG, SVG</div>
                          <div>Max size: 5MB per image</div>
                        </div>
                      </div>

                      {/* Images Display */}
                      {loadingImages ? (
                        <div style={{ 
                          textAlign: "center", 
                          color: "#666", 
                          padding: "20px",
                          background: "#f8f9fa",
                          borderRadius: "8px"
                        }}>
                          Loading images...
                        </div>
                      ) : images.length > 0 ? (
                        <div style={{ 
                          display: "grid", 
                          gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", 
                          gap: "16px",
                          maxHeight: "300px",
                          overflowY: "auto",
                          padding: "8px"
                        }}>
                          {images.map((image, index) => {
                            const selected = isImageSelected(image.path);
                            return (
                            <div key={image.path || index} style={{ 
                              position: "relative",
                              transition: "transform 0.2s ease",
                              cursor: "pointer"
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
                            onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                            >
                              <img
                                src={image.url}
                                alt={`Treatment image ${index + 1}`}
                                style={{
                                  width: "100%",
                                  height: "120px",
                                  objectFit: "cover",
                                  borderRadius: "12px",
                                  cursor: "pointer",
                                  border: selected ? "3px solid #28a745" : "3px solid #e0e0e0",
                                  transition: "border-color 0.2s ease"
                                }}
                                onClick={() => handleViewImage(image)}
                                title="Click to view full size"
                                onMouseEnter={(e) => {
                                  if (!selected) e.target.style.borderColor = "#28a745";
                                }}
                                onMouseLeave={(e) => {
                                  if (!selected) e.target.style.borderColor = "#e0e0e0";
                                }}
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleImageSelection(image);
                                }}
                                style={{
                                  position: "absolute",
                                  top: "-8px",
                                  right: "-8px",
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "50%",
                                  background: selected ? "#28a745" : "#dc3545",
                                  color: "white",
                                  border: "none",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  fontWeight: "bold",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                  transition: "all 0.2s ease"
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = "scale(1.1)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = "scale(1)";
                                }}
                                title={selected ? "Deselect image" : "Select image"}
                              >
                                {selected ? "✓" : "×"}
                              </button>
                              <div style={{
                                position: "absolute",
                                bottom: "4px",
                                left: "4px",
                                right: "4px",
                                background: "rgba(0,0,0,0.7)",
                                color: "white",
                                fontSize: "10px",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                textAlign: "center",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }}>
                                {image.name.length > 15 ? image.name.substring(0, 15) + "..." : image.name}
                              </div>
                              <div style={{
                                position: "absolute",
                                top: "4px",
                                left: "4px",
                                background: "rgba(0,0,0,0.7)",
                                color: "white",
                                fontSize: "10px",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                fontWeight: "bold"
                              }}>
                                #{index + 1}
                              </div>
                              <div style={{
                                position: "absolute",
                                top: "4px",
                                right: "40px",
                                background: "rgba(0,0,0,0.7)",
                                color: "white",
                                fontSize: "10px",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                opacity: 0.8
                              }}>
                                📅
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ 
                          textAlign: "center", 
                          color: "#666", 
                          padding: "30px 20px",
                          background: "#f8f9fa",
                          borderRadius: "12px",
                          border: "2px dashed #dee2e6"
                        }}>
                          <div style={{ fontSize: "16px", marginBottom: "8px", fontWeight: "500" }}>
                            No treatment images yet
                          </div>
                          <div style={{ fontSize: "14px", opacity: 0.8 }}>
                            Upload photos to document the treatment progress
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionDetails>
                </Accordion>
              </Box>
            ) : (
              <div style={{ color: "#888", textAlign: "center", marginTop: "40px" }}>
                Select a treatment to view details.
              </div>
            )}
          
        </Box>
      </Box>
      {/* Image View Modal */}
      {showImageModal && selectedImage && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.9)",
          zIndex: 2000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px"
        }}>
          <div style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }}>
            {/* Close button */}
            <button
              onClick={() => setShowImageModal(false)}
              style={{
                position: "absolute",
                top: "-50px",
                right: "0",
                background: "white",
                border: "none",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                cursor: "pointer",
                fontSize: "20px",
                fontWeight: "bold",
                color: "#333",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
              }}
            >
              ×
            </button>

            {/* Navigation buttons */}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => {
                    const currentIndex = images.findIndex(img => img.path === selectedImage.path);
                    const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
                    setSelectedImage(images[prevIndex]);
                  }}
                  style={{
                    position: "absolute",
                    left: "-60px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "40px",
                    height: "40px",
                    cursor: "pointer",
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#333",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                  }}
                >
                  ‹
                </button>
                <button
                  onClick={() => {
                    const currentIndex = images.findIndex(img => img.path === selectedImage.path);
                    const nextIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
                    setSelectedImage(images[nextIndex]);
                  }}
                  style={{
                    position: "absolute",
                    right: "-60px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "40px",
                    height: "40px",
                    cursor: "pointer",
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#333",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
                  }}
                >
                  ›
                </button>
              </>
            )}

            {/* Image */}
            <img
              src={selectedImage.url}
              alt="Treatment image"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                borderRadius: "8px"
              }}
            />

            {/* Image info */}
            <div style={{
              position: "absolute",
              bottom: "-40px",
              left: "50%",
              transform: "translateX(-50%)",
              color: "white",
              fontSize: "14px",
              background: "rgba(0,0,0,0.7)",
              padding: "8px 16px",
              borderRadius: "20px",
              textAlign: "center"
            }}>
              {selectedImage.name}
              {images.length > 1 && (
                <span style={{ marginLeft: "10px", opacity: 0.8 }}>
                  ({images.findIndex(img => img.path === selectedImage.path) + 1} of {images.length})
                </span>
              )}
            </div>

            {/* Keyboard navigation hint */}
            <div style={{
              position: "absolute",
              top: "-60px",
              left: "50%",
              transform: "translateX(-50%)",
              color: "white",
              fontSize: "12px",
              opacity: 0.7,
              textAlign: "center"
            }}>
              Use arrow keys to navigate • ESC to close
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbarConfig.isOpen}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          variant="filled"
          onClose={closeSnackbar}
          severity={snackbarConfig.severity}
          icon={false}
          sx={{
            backgroundColor: snackbarConfig.severity === "success" ? "#558363" : "#9F4223",
            color: "#fff",
            fontWeight: 600,
            letterSpacing: 0.2,
            borderRadius: "12px",
            padding: "8px 16px",
            minWidth: "auto",
            width: "auto",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center"
          }}
        >
          {snackbarConfig.message}
        </MuiAlert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      {deleteModalConfig.isOpen && (deleteModalConfig.images?.length || 0) > 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "30px",
              textAlign: "center",
              boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              maxWidth: "500px",
              width: "100%",
            }}
          >
            <h3
              style={{
                marginBottom: "20px",
                fontWeight: "bold",
                color: "#333",
                fontSize: "20px",
              }}
            >
              Confirm Deletion
            </h3>

            <div
              style={{
                marginBottom: "25px",
                textAlign: "center",
                lineHeight: "1.5",
              }}
            >
              <p
                style={{
                  marginBottom: "12px",
                  color: "#555",
                  fontSize: "15px",
                }}
              >
                Are you sure you want to delete {deleteModalConfig.images.length > 1 ? "the selected images" : "this image"}?
              </p>

              <div
                style={{
                  background: "#f8f9fa",
                  border: "1px solid #e9ecef",
                  borderRadius: "6px",
                  padding: "12px",
                  marginBottom: "12px",
                  maxHeight: "180px",
                  overflowY: "auto",
                }}
              >
                <div
                  style={{
                    fontSize: "13px",
                    color: "#666",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                >
                  {deleteModalConfig.images.length > 1 ? "Images:" : "Filename:"}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#495057",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    hyphens: "auto",
                  }}
                >
                  {deleteModalConfig.images.slice(0, 8).map((img, idx) => (
                    <div key={img.path || idx} style={{ marginBottom: "6px" }}>
                      {img.name}
                    </div>
                  ))}
                  {deleteModalConfig.images.length > 8 && (
                    <div style={{ fontSize: "12px", color: "#888" }}>
                      +{deleteModalConfig.images.length - 8} more
                    </div>
                  )}
                </div>
              </div>

              <p
                style={{
                  margin: 0,
                  color: "#dc3545",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                ⚠️ This action cannot be undone.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "15px",
              }}
            >
              <button
                onClick={handleConfirmDelete}
                style={{
                  background: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                  minWidth: "120px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#c82333";
                  e.target.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "#dc3545";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                Delete
              </button>
              <button
                onClick={closeDeleteModal}
                style={{
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                  minWidth: "120px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#5a6268";
                  e.target.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "#6c757d";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default PersonnelTreatmentHistoryModal;